import { STATUS } from "@/constants/statuses";
import RewardsVipTiersApiService from "@apps/rewards/modules/VipTiers/api_service/RewardsVipTiersApiService";
import {
    getLoadingStatus,
    selectRequestStatus,
} from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import { Observable, catchError, combineLatest, filter, map, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { rewardsApplicationLoggerToken } from "../../../tokens";
import type { RewardsProgramAccountEntity } from "../../ProgramAccount/models/RewardsProgramAccountEntity";
import RewardsProgramAccountQuery from "../../ProgramAccount/query/RewardsProgramAccountQuery";
import type {
    RewardsVipTierEntity,
    RewardsVipTierProgressResponseType,
    RewardsVipTiersResponseType,
} from "../models/RewardsVipTiersEntities";
import RewardsVipTierProgressQuery from "../queries/RewardsVipTierProgressQuery";
import RewardsVipTiersQuery from "../queries/RewardsVipTiersQuery";
import RewardsVipTierProgressStore from "../stores/RewardsVipTierProgressStore";
import RewardsVipTiersStore from "../stores/RewardsVipTiersStore";
import type {
    ObservableCustomerNextTierType,
    RewardsVipTiersQueryParams,
} from "../types";

@singleton()
class RewardsVipTiersService {
    private readonly combineTierListAndProgramAccount$: Observable<
        [RewardsVipTierEntity[], RewardsProgramAccountEntity | null]
    >;
    public readonly customerNextTier$: ObservableCustomerNextTierType;

    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly rewardsVipTiersApiService: RewardsVipTiersApiService,
        private readonly rewardsVipTiersStore: RewardsVipTiersStore,
        private readonly rewardsVipTiersQuery: RewardsVipTiersQuery,
        private readonly rewardsVipTierProgressStore: RewardsVipTierProgressStore,
        private readonly rewardsVipTierProgressQuery: RewardsVipTierProgressQuery,
        private readonly rewardsProgramAccountQuery: RewardsProgramAccountQuery
    ) {
        this.combineTierListAndProgramAccount$ = combineLatest([
            this.rewardsVipTiersQuery.tierList$,
            this.rewardsProgramAccountQuery.programAccount$,
        ]);

        this.customerNextTier$ = this.selectCustomerNextTier();
    }

    private getCustomerTierIndex = (
        tierList: RewardsVipTierEntity[],
        customerTierId?: number | null
    ): number => tierList.findIndex((tier) => tier.id === customerTierId);

    private selectCustomerNextTier(): ObservableCustomerNextTierType {
        return this.combineTierListAndProgramAccount$.pipe(
            filter(
                ([tierList, programAccount]) =>
                    !!tierList.length && !!programAccount
            ),
            map(([tierList, programAccount]) => {
                const nextIndex = this.getCustomerTierIndex(
                    tierList,
                    programAccount?.tier?.currentTier?.id
                );

                return tierList[nextIndex + 1];
            })
        );
    }

    public selectAvailableTiers(): Observable<RewardsVipTierEntity[]> {
        return this.combineTierListAndProgramAccount$.pipe(
            map(([tierList, programAccount]) =>
                tierList.slice(
                    Math.max(
                        0,
                        this.getCustomerTierIndex(
                            tierList,
                            programAccount?.tier?.currentTier?.id
                        )
                    )
                )
            )
        );
    }

    public selectSingleTierPercentageProgress(): Observable<number> {
        return combineLatest([
            this.rewardsVipTierProgressQuery.tierProgress$,
            this.customerNextTier$,
        ]).pipe(
            map(([tierProgress, nextTier]) =>
                nextTier && tierProgress
                    ? Number(
                          (
                              (tierProgress / nextTier.conditionValue) *
                              100
                          ).toFixed(2)
                      )
                    : 0
            )
        );
    }

    public selectTiersWithPercentageProgress(
        extendedTierList$: Observable<
            Array<{ title: string; value: number; id: number }>
        >
    ) {
        return combineLatest([
            this.rewardsVipTierProgressQuery.tierProgress$,
            extendedTierList$,
        ]).pipe(
            map(([tierProgress, tierList]) => {
                return tierList.map((tier, index) => {
                    const currentCondition = tier.value;
                    const nextCondition =
                        tierList[index + 1]?.value || currentCondition;
                    let percentageProgress = 0;

                    if (tierProgress >= nextCondition) {
                        percentageProgress = 100;
                    } else if (tierProgress < currentCondition) {
                        percentageProgress = 0;
                    } else {
                        percentageProgress =
                            ((tierProgress - currentCondition) /
                                (nextCondition - currentCondition)) *
                            100;
                    }

                    return {
                        ...tier,
                        percentageProgress: Number(
                            percentageProgress.toFixed(2)
                        ),
                    };
                });
            })
        );
    }

    public getTiers(
        queryParams: RewardsVipTiersQueryParams
    ): Observable<RewardsVipTiersResponseType> {
        this.logger.debug("RewardsVipTiersService.getTiers is started");

        const status = this.rewardsVipTiersStore.getStatus();
        this.rewardsVipTiersStore.setStatus(getLoadingStatus(status));

        return this.rewardsVipTiersApiService.getTiers(queryParams).pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTiers is errored",
                    error
                );
                this.rewardsVipTiersStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug("RewardsVipTiersService.getTiers is ready");
                //Store Update
                applyTransaction(() => {
                    this.rewardsVipTiersStore.update((state) => ({
                        ...state,
                        tierList: value.tiers,
                    }));
                    this.rewardsVipTiersStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    public getTierProgress(): Observable<RewardsVipTierProgressResponseType> {
        this.logger.debug("RewardsVipTiersService.getTierProgress is started");

        const status = this.rewardsVipTierProgressStore.getStatus();
        this.rewardsVipTierProgressStore.setStatus(getLoadingStatus(status));

        return this.rewardsVipTiersApiService.getTierProgress().pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTierProgress is errored",
                    error
                );
                this.rewardsVipTierProgressStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug(
                    "RewardsVipTiersService.getTierProgress is ready"
                );
                //Store Update
                applyTransaction(() => {
                    this.rewardsVipTierProgressStore.update((state) => ({
                        ...state,
                        tierProgress: value.progress,
                    }));
                    this.rewardsVipTierProgressStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    public isInitialLoading(): Observable<boolean> {
        return selectRequestStatus(
            [
                this.rewardsVipTiersQuery.status$,
                this.rewardsVipTierProgressQuery.status$,
            ],
            STATUS.INITIAL_LOADING
        );
    }
}

export default RewardsVipTiersService;
